import { Session } from 'next-auth';
import { SubriteUser } from '../types';

export interface AuthData {
  session: Session | null;
  isLoggedIn: boolean;
  user?: SubriteUser;
  hasAccess: boolean;
  hasLimitedAccess: boolean;
  isAdmin: boolean;
  isActiveSubscriber: boolean;
  checkPackage: (packageId: number) => boolean;
  checkProduct: (productId: number) => boolean;
  checkChannel: (contentId: number) => boolean;
  checkPodcast: (podcastId: number) => boolean;
  checkConsent: (consentId: number) => boolean;
}

//  disable unrestricted admin access if needed with env variable, useful for testing and security
const adminDisabled = process?.env?.NEXT_PUBLIC_SUBRITE_ADMIN_DISABLED === 'true';

export function transformSession(session: Session | null): AuthData {
  if (!session) {
    return {
      isLoggedIn: false,
      user: undefined,
      hasAccess: false,
      isAdmin: false,
      isActiveSubscriber: false,
      hasLimitedAccess: false,
      session: null,
      checkPackage: (_) => false,
      checkProduct: (_) => false,
      checkChannel: (_) => false,
      checkPodcast: (_) => false,
      checkConsent: (_) => false,
    };
  }
  if (session?.googlebot) {
    return {
      isLoggedIn: false,
      user: undefined,
      hasAccess: true,
      hasLimitedAccess: true,
      isAdmin: false,
      isActiveSubscriber: false,
      session: null,
      checkPackage: (_) => true,
      checkProduct: (_) => true,
      checkChannel: (_) => true,
      checkPodcast: (_) => true,
      checkConsent: (_) => true,
    };
  }

  if (session?.draftMode) {
    return {
      isLoggedIn: false,
      user: undefined,
      hasAccess: true,
      hasLimitedAccess: true,
      isAdmin: false,
      isActiveSubscriber: false,
      session: null,
      checkPackage: (_) => true,
      checkProduct: (_) => true,
      checkChannel: (_) => true,
      checkPodcast: (_) => true,
      checkConsent: (_) => true,
    };
  }

  const data = session;
  const isLoggedIn = !!data?.user;
  const user = data?.user;
  const isAdmin = adminDisabled ? false : (user?.userType?.includes('admin') ?? false); // if admin disabled, isAdmin is always false
  const isActiveSubscriber = user?.subscriptions?.some((sub) => sub.status === 'active') || false;
  const hasAccess = Boolean(isAdmin || isActiveSubscriber);
  const hasLimitedAccess = hasAccess || isLoggedIn;

  /**
   * Check if the user has consented to the consent. Returns true if the user has consented, false otherwise
   * @param consentId Consent ID from Subrite
   * @returns true if consentStatus === 'subscribed', false otherwise
   */
  const checkConsent = (consentId: number) => {
    const access = user?.consents?.find((consent) => {
      return consent?.id === consentId;
    });

    if (access?.consentStatus === 'accepted') {
      return true;
    }
    return false;
  };

  const checkChannel = (channelId: number) => {
    const access = user?.contentChannels?.find((channel) => {
      return channel?.id === channelId;
    });
    return Boolean(isAdmin || access);
  };

  const checkPodcast = (podcastId: number) => {
    const access = user?.podcasts?.find((podcast) => {
      return podcast?.id === podcastId;
    });
    return Boolean(isAdmin || access);
  };

  const checkPackage = (packageId: number) => {
    const access = user?.subscriptions?.find((subscription) => {
      return subscription.status === 'active' && subscription.packageId === packageId;
    });
    return Boolean(isAdmin || access);
  };

  const checkProduct = (productId: number) => {
    const productIds = user?.subscriptions
      ?.map((subscription) => subscription?.subscriptionProducts?.map((product) => product.id))
      .flat();
    const access = productIds?.includes(productId);

    return Boolean(isAdmin || access);
  };

  return {
    isLoggedIn,
    user,
    session: data,
    hasAccess,
    hasLimitedAccess,
    isAdmin,
    isActiveSubscriber,
    checkPackage,
    checkProduct,
    checkChannel,
    checkPodcast,
    checkConsent,
  };
}
